import { type ColumnDef } from "@tanstack/react-table";
import { IconChevronRight, IconPackage } from "@tabler/icons-react";

import { DataTable } from "@/components/ui/data-table";
import { formatAmount, formatNumber } from "@/utils/formatters";

interface Program {
  id: string;
  commissionType: string;
  commissionValue: string;
}

export interface Product {
  id: string;
  title: string;
  image: string;
  price: string;
  description: string;
  inventory: number;
  program: Program;
  stats: {
    affiliateSalesCountTotal: number;
    affiliateSalesCountThisMonth: number;
    campaignSalesCountTotal: number;
    campaignSalesCountThisMonth: number;
  };
}

interface ProductTableProps {
  products: Product[];
  onProductClick?: (productId: string) => void;
  isLoading?: boolean;
}

/** "15%" or "$5" depending on the program's commission type. */
export function formatCommission(program: Program | undefined) {
  if (!program) return "—";
  const value = Number(program.commissionValue) || 0;
  return program.commissionType === "FIXED" ? formatAmount(value) : `${value}%`;
}

/** Product thumbnail plus name: the identity cell every product table shares. */
export function ProductIdentity({
  title,
  image,
  size = "size-7",
}: {
  title: string;
  image: string | null;
  size?: string;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        className={`flex ${size} shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground`}
      >
        {image ? (
          <img src={image} alt="" className="h-full w-full object-cover" />
        ) : (
          <IconPackage size={14} stroke={1.75} />
        )}
      </span>
      <span className="block max-w-[18rem] truncate font-medium text-foreground" title={title}>
        {title}
      </span>
    </span>
  );
}

const thumbSkeleton = (
  <span className="flex items-center gap-2.5">
    <span className="size-7 rounded bg-muted animate-pulse" />
    <span className="h-3.5 w-40 rounded bg-muted animate-pulse" />
  </span>
);

const columns: ColumnDef<Product>[] = [
  {
    accessorKey: "title",
    header: "Product",
    meta: { skeleton: thumbSkeleton },
    cell: ({ row }) => <ProductIdentity title={row.original.title} image={row.original.image} />,
  },
  {
    accessorKey: "price",
    header: "Price",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {formatAmount(Number(row.original.price) || 0)}
      </span>
    ),
  },
  {
    id: "affiliateSales",
    header: "Affiliate sales",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {formatNumber(row.original.stats.affiliateSalesCountTotal)}
      </span>
    ),
  },
  {
    id: "campaignSales",
    header: "Campaign sales",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {formatNumber(row.original.stats.campaignSalesCountTotal)}
      </span>
    ),
  },
  {
    id: "thisMonth",
    header: "This month",
    meta: { fit: true },
    cell: ({ row }) => {
      const { stats } = row.original;
      const n = stats.affiliateSalesCountThisMonth + stats.campaignSalesCountThisMonth;
      return (
        <span className={n === 0 ? "tabular-nums text-muted-foreground" : "tabular-nums text-foreground"}>
          {formatNumber(n)}
        </span>
      );
    },
  },
  {
    id: "commission",
    header: "Commission",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">{formatCommission(row.original.program)}</span>
    ),
  },
  {
    accessorKey: "inventory",
    header: "Stock",
    meta: { fit: true },
    cell: ({ row }) =>
      row.original.inventory > 0 ? (
        <span className="tabular-nums text-foreground">{formatNumber(row.original.inventory)}</span>
      ) : (
        <span className="text-muted-foreground">Out of stock</span>
      ),
  },
  {
    id: "open",
    header: "",
    meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
    cell: () => (
      <IconChevronRight
        size={16}
        stroke={1.75}
        className="inline-block text-muted-foreground"
        aria-hidden
      />
    ),
  },
];

export function ProductTable({ products, onProductClick, isLoading }: ProductTableProps) {
  return (
    <DataTable
      columns={columns}
      data={products}
      isLoading={isLoading}
      getRowId={(row) => row.id}
      emptyText="No products synced yet"
      onRowClick={onProductClick ? (row) => onProductClick(row.id) : undefined}
    />
  );
}
